import { Title, eventDecimal, eventInt, eventTimestamp, type EventMap } from '@simple-life-medical/med-analytics-ts-web'
import analytics from '@/analytics/client'
import type { RxStatus } from '@/api/medBilling'
import { useLocationStore } from '@/store/location'

// `source` is the raw `?from` arrival value, verbatim (landing | telegra | app | email),
// omitted when absent. It lives in the location store, which persists it across the Auth0
// round-trip, so events on /status etc. still carry it.
function source(): string | undefined {
	return useLocationStore().from ?? undefined
}

function withSource<T extends object>(payload: T): T & { source?: string } {
	const s = source()
	return s ? { ...payload, source: s } : payload
}

export const STEP_TITLE = {
	// `/cancel/scope` (DEV-34808). Per the GLP Cancellation Funnel Logging spec the slug names the
	// *screen* ("Your prescription is currently under clinical review"), not the question on it —
	// so it is `PRESCRIPTION_IS_UNDER_REVIEW`, not `WHAT_TO_CANCEL` (the pre-spec working name).
	PRESCRIPTION_IS_UNDER_REVIEW: 'PRESCRIPTION_IS_UNDER_REVIEW',
	WHY_CANCELLING: 'WHY_CANCELLING',
	TRY_AGAIN: 'TRY_AGAIN',
	// The commitment flow (AL-15098, Jet CR 73): /cancel/commitment/change, /options, /end-now and
	// /refund, /end-after, the "Processing your payment…" overlay, /failed.
	WHAT_TO_CHANGE: 'WHAT_TO_CHANGE',
	WHEN_TO_CANCEL: 'WHEN_TO_CANCEL',
	CANCEL_RIGHT_NOW_SUMMARY: 'CANCEL_RIGHT_NOW_SUMMARY',
	CANCEL_AFTER_SCHEDULED_PAYMENTS_SUMMARY: 'CANCEL_AFTER_SCHEDULED_PAYMENTS_SUMMARY',
	PROCESSING_PAYMENT: 'PROCESSING_PAYMENT',
	PAYMENT_UNSUCCESSFUL: 'PAYMENT_UNSUCCESSFUL',
	// The "Something went wrong" modal over any screen whose cancel call failed (agreed with analytics 2026-10-21).
	CANCELLATION_FAILED: 'CANCELLATION_FAILED',
	DO_YOU_STILL_WANT_TO_CANCEL: 'DO_YOU_STILL_WANT_TO_CANCEL',
	// /cancel/done, every outcome and every plan (CR 73: replaces SUBSCRIPTION_HAS_BEEN_CANCELLED).
	CANCELLATION_CONFIRMED: 'CANCELLATION_CONFIRMED',
	FEEDBACK: 'FEEDBACK',
} as const
export type StepTitle = (typeof STEP_TITLE)[keyof typeof STEP_TITLE]

export function trackCancellationSessionStart(cancellationSessionId: string): void {
	analytics.track('CancellationSessionStart', withSource({ cancellation_session_id: cancellationSessionId }))
}

/**
 * Callers keep plain JS types — epoch milliseconds for the dates, a decimal string for the price.
 * `trackSubscriptionDetailsView` brands them for the SDK below.
 */
export interface SubscriptionDetailsPayload {
	status?: string
	is_trial?: boolean
	subscription_id?: string
	product_id?: string
	/** epoch ms — converted to the SDK's EventTimestamp here */
	renewal_date?: number
	expiration_date?: number
	last_billed_date?: number
	subscription_duration?: string
	/** decimal string ("449.00") — converted to the SDK's EventDecimal here */
	sub_price_usd?: string
	// CR 72 — the plan; `commitment_*` only on a commitment (left out, not sent empty, otherwise).
	is_commitment?: boolean
	plan_payment_type?: 'monthly' | 'paid_in_full'
	plan_months?: number
	/** epoch ms */
	commitment_end_date?: number
	commitment_orders_shipped?: number
	commitment_payments_paid?: number
	/** decimal string ("548.00") */
	commitment_balance_usd?: string
	/**
	 * Prescription review state, in the **domain** spelling (`in_review` / `approved` / `rejected`,
	 * i.e. what the badge shows), not billing's wire `review`. The SDK types the field as a bare
	 * `string`, so nothing enforces this — hence the `RxStatus` type here. Omitted rather than sent
	 * empty whenever the badge is also hidden: no clinical order, a backend older than
	 * med-billing-engine#204, or a plan status outside `active` / `awaiting_approval` (the store's
	 * `getRxStatus` is the single gate for both).
	 */
	rx_status?: RxStatus
}

/**
 * med-analytics-ts 0.3.x types timestamp/decimal event fields as branded primitives
 * (`EventTimestamp` = epoch ms from a `Date`, `EventDecimal` = decimal string), so they have to
 * go through the SDK's `eventTimestamp` / `eventDecimal` constructors. Only branding — no value
 * change; everything else (incl. `rx_status`) rides along untouched. Note the SDK silently drops
 * fields it doesn't know, so anything added here has to exist in the package's event contract first.
 */
export function trackSubscriptionDetailsView(payload: SubscriptionDetailsPayload): void {
	// SDK ≥0.3.x types dates as EventTimestamp and prices as EventDecimal; the view keeps
	// passing plain epoch-ms numbers / decimal strings and this wrapper converts.
	const {
		renewal_date,
		expiration_date,
		last_billed_date,
		sub_price_usd,
		plan_months,
		commitment_end_date,
		commitment_orders_shipped,
		commitment_payments_paid,
		commitment_balance_usd,
		...rest
	} = payload
	const event: EventMap['SubscriptionDetailsScreenView'] = {
		...rest,
		renewal_date: renewal_date != null ? eventTimestamp(new Date(renewal_date)) : undefined,
		expiration_date: expiration_date != null ? eventTimestamp(new Date(expiration_date)) : undefined,
		last_billed_date: last_billed_date != null ? eventTimestamp(new Date(last_billed_date)) : undefined,
		sub_price_usd: sub_price_usd != null ? eventDecimal(sub_price_usd) : undefined,
		...(plan_months != null ? { plan_months: eventInt(plan_months) } : {}),
		...(commitment_end_date != null ? { commitment_end_date: eventTimestamp(new Date(commitment_end_date)) } : {}),
		...(commitment_orders_shipped != null ? { commitment_orders_shipped: eventInt(commitment_orders_shipped) } : {}),
		...(commitment_payments_paid != null ? { commitment_payments_paid: eventInt(commitment_payments_paid) } : {}),
		...(commitment_balance_usd != null ? { commitment_balance_usd: eventDecimal(commitment_balance_usd) } : {}),
	}
	analytics.track('SubscriptionDetailsScreenView', withSource(event))
}

/**
 * `data` (CR 73): what the screen shows, as JSON — `{ commitment }` on the commitment amount screens;
 * left out everywhere else.
 */
export function trackCancellationStepShow(title: StepTitle, isBackwards: boolean, data?: object): void {
	const event: EventMap['CancellationFlowStepShow'] = {
		title,
		preselected_responses: [],
		is_backwards: isBackwards,
		...(data ? { data: JSON.stringify(data) } : {}),
	}
	analytics.track('CancellationFlowStepShow', withSource(event))
}
export function trackCancellationStepDone(
	title: StepTitle,
	data: { responses: string[]; text: string; commitment?: object },
	isBackwards: boolean,
	isStepBack: boolean,
): void {
	analytics.track(
		'CancellationFlowStepDone',
		withSource({ title, data: JSON.stringify(data), is_backwards: isBackwards, is_step_back: isStepBack }),
	)
}

export interface SubscriptionStateChangedPayload {
	subscription_type?: string
	provider?: string
	product_id?: string
	payment_method?: string
	subscription_duration?: string
	from?: string
	to?: string
	cancellation_type?: CancellationTypeLabel
}
/**
 * CR 77 (as agreed with analytics 2026-10-21): what the cancel means, not the billing value —
 * `hard`: the plan closes at once, settled today (fee / refund / the rest) or with nothing to settle (under
 * review); `soft`: renewal off, no more payments; `soft_commitment`: renewal off, payments go on to the
 * commitment end; `supply_end`: "Cancel after N payments" — the fee in instalments, the plan ends with
 * the current supply. When access ends is `expiration_date`, not this.
 */
export type CancellationTypeLabel = 'hard' | 'soft' | 'soft_commitment' | 'supply_end'
export function trackSubscriptionStateChanged(payload: SubscriptionStateChangedPayload): void {
	analytics.track('SubscriptionStateChanged', withSource(payload))
}

export function trackSupportEntryPoint(entryPoint: string): void {
	analytics.track('SupportEntryPointDone', { entry_point: entryPoint })
}

// ButtonClick carries the `Title` enum via the tracking header (it's Ux-tagged);
// semantic wrappers keep views from importing the enum directly.
function trackTitleButtonClick(title: Title): void {
	analytics.track('ButtonClick', {}, { tracking: { title, path: [] } })
}
export function trackManageSubscriptionClick(): void {
	trackTitleButtonClick(Title.MANAGE_SUBSCRIPTION_BUTTON)
}
export function trackLogoutClick(): void {
	trackTitleButtonClick(Title.SUBSCRIPTION_DETAILS_LOGOUT_BUTTON)
}
/** CR 79: "Pay $X and cancel today" on /status. */
export function trackPayRemainingBalanceClick(): void {
	trackTitleButtonClick(Title.SUBSCRIPTION_DETAILS_PAY_REMAINING_BALANCE_BUTTON)
}
/** CR 79: "Contact clinician" on /status. */
export function trackContactClinicianClick(): void {
	trackTitleButtonClick(Title.SUBSCRIPTION_DETAILS_CONTACT_CLINICIAN_BUTTON)
}

// ---------------------------------------------------------------------------
// Payment-method change (DEV-35117; analytics spec: AL-14225 comment 111788).
// Requires SDK >= 0.3.10 — the release generated from kova_event_schema Jet
// change-request #54 (med-analytics-ts PR #44), which added the
// UpdatePaymentMethodPaywallShow/Done events and Title ids 65-70.
// ---------------------------------------------------------------------------

// 'direct' is an owner addition (2026-08-14) on top of the AL-14225 spec comment: it marks
// arrivals that came neither from the /status button nor from the failed screen's retry —
// CRM/email deep links, bookmarks, the post-login redirect. The schema field is a plain
// optional string, so the extra value passes the SDK encoder as-is.
export type UpdatePaymentEntryPoint =
	| 'subscription_details_button'
	| 'update_payment_method_unsuccess_screen'
	| CancellationPaymentEntryPoint
	| 'direct'
/** CR 75 / 76: the card change opened from the commitment flow (marked in the history state). */
export type CancellationPaymentEntryPoint =
	| 'cancellation_what_to_change_screen'
	| 'cancellation_payment_unsuccessful_screen'
export const CANCELLATION_PAYMENT_ENTRY_POINTS: readonly CancellationPaymentEntryPoint[] = [
	'cancellation_what_to_change_screen',
	'cancellation_payment_unsuccessful_screen',
]
export type UpdatePaymentScreenTitle =
	| 'UPDATE_PAYMENT_METHOD_LOADING_SCREEN'
	| 'UPDATE_PAYMENT_METHOD_SUCCESS_SCREEN'
	| 'UPDATE_PAYMENT_METHOD_UNSUCCESS_SCREEN'

export function trackChangePaymentMethodClick(): void {
	trackTitleButtonClick(Title.SUBSCRIPTION_DETAILS_CHANGE_PAYMENT_METHOD_BUTTON)
}

export function trackUpdatePaymentPaywallBackClick(): void {
	trackTitleButtonClick(Title.UPDATE_PAYMENT_PAYWALL_BACK_BUTTON)
}

export function trackUpdatePaymentSuccessDoneClick(): void {
	trackTitleButtonClick(Title.UPDATE_PAYMENT_METHOD_SUCCESS_SCREEN_DONE_BUTTON)
}

export interface UpdatePaymentPaywallShowPayload {
	entry_point: UpdatePaymentEntryPoint
	previous_method?: string
	available_methods: string[]
}
export function trackUpdatePaymentMethodPaywallShow(payload: UpdatePaymentPaywallShowPayload): void {
	analytics.track('UpdatePaymentMethodPaywallShow', { ...payload })
}

export interface UpdatePaymentPaywallDonePayload {
	entry_point: UpdatePaymentEntryPoint
	selected_method: string
	previous_method?: string
}
export function trackUpdatePaymentMethodPaywallDone(payload: UpdatePaymentPaywallDonePayload): void {
	analytics.track('UpdatePaymentMethodPaywallDone', { ...payload })
}

// The screen identity travels in the Ux tracking header (like ButtonClick), so the
// string names used by the views map onto the SDK's numeric Title enum here.
const UPDATE_PAYMENT_SCREEN_TITLES: Record<UpdatePaymentScreenTitle, Title> = {
	UPDATE_PAYMENT_METHOD_LOADING_SCREEN: Title.UPDATE_PAYMENT_METHOD_LOADING_SCREEN,
	UPDATE_PAYMENT_METHOD_SUCCESS_SCREEN: Title.UPDATE_PAYMENT_METHOD_SUCCESS_SCREEN,
	UPDATE_PAYMENT_METHOD_UNSUCCESS_SCREEN: Title.UPDATE_PAYMENT_METHOD_UNSUCCESS_SCREEN,
}
export function trackUpdatePaymentScreenView(title: UpdatePaymentScreenTitle): void {
	analytics.track('ScreenView', {}, { tracking: { title: UPDATE_PAYMENT_SCREEN_TITLES[title], path: [] } })
}

// ---------------------------------------------------------------------------
// Pre-Rx cancellation branch (DEV-34808).
// ---------------------------------------------------------------------------

/*
 * The "Before you go…" modal on /status (shown only while the Rx is under review). `PopupView` /
 * `PopupClose` are Ux-tagged like `ButtonClick`, so their identity is the `Title` in the tracking
 * header — the payload itself is empty. The two events, the three `BEFORE_YOU_GO` titles (ids
 * 71-73) and `rx_status` on `SubscriptionDetailsScreenView` all arrived in med-analytics-ts 0.3.11,
 * which is why this branch bumps the vendored SDK from 0.3.10.
 *
 * Close semantics: `PopupClose` marks the modal being **dismissed** (× / backdrop / "Got it") — the
 * save path. "Continue with cancellation" is a transition, not a dismissal, so it emits only its
 * `ButtonClick`; that keeps `PopupClose / PopupView` readable as the save rate.
 */
export function trackBeforeYouGoPopupView(): void {
	analytics.track('PopupView', {}, { tracking: { title: Title.CANCELLATION_FLOW_BEFORE_YOU_GO_POPUP, path: [] } })
}
export function trackBeforeYouGoPopupClose(): void {
	analytics.track('PopupClose', {}, { tracking: { title: Title.CANCELLATION_FLOW_BEFORE_YOU_GO_POPUP, path: [] } })
}
export function trackBeforeYouGoGotItClick(): void {
	trackTitleButtonClick(Title.CANCELLATION_FLOW_BEFORE_YOU_GO_GOT_IT_BUTTON)
}
export function trackBeforeYouGoContinueClick(): void {
	trackTitleButtonClick(Title.CANCELLATION_FLOW_BEFORE_YOU_GO_CONTINUE_WITH_CANCELLATION_BUTTON)
}
